import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { LoaderFunctionArgs } from '@remix-run/node';
import { redirect } from '@remix-run/node';
import Button from '~/ui-apps/design-system/atoms/Button';
import PasswordlessAuthCallout from '~/ui-apps/design-system/molecules/PasswordlessAuthCallout';
import { Form, Link, useLoaderData } from '@remix-run/react';
import axios from 'axios';
import { POST_AUTH_LANDING_PATH } from '~/utilities/postAuthLanding';
import { getCachedAuthData } from '~/utilities/secured-route.server';

export async function loader({ request }: LoaderFunctionArgs) {
  const authData = await getCachedAuthData(request);
  if (authData.isAuthenticated) {
    return redirect(POST_AUTH_LANDING_PATH);
  }
  return Response.json({
    turnstileSiteKey: process.env.TURNSTILE_SITE_KEY ?? '',
  });
}

// Extend window type for Turnstile
declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: {
        sitekey: string;
        callback?: (token: string) => void;
        'expired-callback'?: () => void;
        'error-callback'?: () => void;
        theme?: 'light' | 'dark' | 'auto';
      }) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

const SignIn: React.FC = () => {
  const { turnstileSiteKey } = useLoaderData<typeof loader>();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmittedAndUserCreated, setIsSubmittedAndUserCreated] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);
  const turnstileWidgetId = useRef<string | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement>(null);

  // Load Turnstile script
  useEffect(() => {
    if (!turnstileSiteKey) return;

    // Check if script is already loaded
    if (window.turnstile) {
      setTurnstileLoaded(true);
      return;
    }

    // Check if script tag already exists
    const existingScript = document.querySelector('script[src*="turnstile"]');
    if (existingScript) {
      // Script exists, wait for it to load
      window.onTurnstileLoad = () => setTurnstileLoaded(true);
      return;
    }

    // Create and add the script
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
    script.async = true;
    script.defer = true;

    window.onTurnstileLoad = () => setTurnstileLoaded(true);

    document.head.appendChild(script);

    return () => {
      // Cleanup widget on unmount
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current);
      }
    };
  }, [turnstileSiteKey]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Track whether a valid email has been entered
  const isValidEmail = validateEmail(email);

  // Render Turnstile widget when script is loaded AND a valid email is entered
  useEffect(() => {
    if (!isValidEmail || !turnstileLoaded || !window.turnstile || !turnstileContainerRef.current || !turnstileSiteKey) {
      return;
    }

    // Don't re-render if widget already exists
    if (turnstileWidgetId.current) {
      return;
    }

    turnstileWidgetId.current = window.turnstile.render(turnstileContainerRef.current, {
      sitekey: turnstileSiteKey,
      callback: (token: string) => {
        setTurnstileToken(token);
      },
      'expired-callback': () => {
        setTurnstileToken(null);
      },
      'error-callback': () => {
        setTurnstileToken(null);
      },
      theme: 'light',
    });
  }, [isValidEmail, turnstileLoaded, turnstileSiteKey]);

  // Clean up widget when email becomes invalid (container unmounts)
  useEffect(() => {
    if (!isValidEmail && turnstileWidgetId.current && window.turnstile) {
      window.turnstile.remove(turnstileWidgetId.current);
      turnstileWidgetId.current = null;
      setTurnstileToken(null);
    }
  }, [isValidEmail]);

  // Reset Turnstile widget
  const resetTurnstile = useCallback(() => {
    if (turnstileWidgetId.current && window.turnstile) {
      window.turnstile.reset(turnstileWidgetId.current);
      setTurnstileToken(null);
    }
  }, []);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const userEnteredEmail = e.target.value;
    setEmail(userEnteredEmail);
    const isEmailValid = validateEmail(userEnteredEmail);

    if (userEnteredEmail.length > 0 && !isEmailValid) {
      setMessage('Please enter a valid email address');
    } else {
      setMessage('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);

    if (!validateEmail(email)) {
      setMessage('Please enter a valid email address');
      return;
    }

    // Check for Turnstile token
    if (!turnstileToken) {
      setMessage('Please complete the verification');
      return;
    }

    try {
      // check if email is already in the database
      const verifyResponse = await axios.get(`/api/users/verify?email=${email}`);
      console.log('signin api response', verifyResponse.data);
      if (!verifyResponse.data.exists) {
        // setMessage('An account does not exist with this email. Please sign up first.');
        // return;
        const signupResponse = await axios.post('/api/sign-up', { email, redirectUrl: POST_AUTH_LANDING_PATH, turnstileToken });
        const data = signupResponse.data;

        if (data.status === "success") {
          setMessage("We've emailed you a secured sign-in link. If it's not there, check your spam folders.");
          setIsSubmittedAndUserCreated(true);
        } else {
          setMessage(data.error || data.status || 'An error occurred. Please try again.');
          resetTurnstile(); // Reset widget so user can try again
        }
      } else {
        const signupResponse = await axios.post('/api/sign-in', { email, redirectUrl: POST_AUTH_LANDING_PATH, turnstileToken });
        const data = signupResponse.data;

        if (data.status === "success") {
          setMessage("We've emailed you a secured sign-in link. If it's not there, check your spam folders.");
          setIsSubmittedAndUserCreated(true);
        } else {
          setMessage(data.error || data.status || 'An error occurred. Please try again.');
          resetTurnstile(); // Reset widget so user can try again
        }
      }


    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          // Turnstile verification failed
          setMessage(error.response.data?.error || 'Verification failed. Please try again.');
          resetTurnstile();
        } else if (error.response?.status === 404) {
          setMessage('Service temporarily unavailable. Please try again later.');
          resetTurnstile();
        } else {
          setMessage('An error occurred while processing your request. Please try again.');
          resetTurnstile();
        }
      } else {
        setMessage('An unexpected error occurred. Please try again.');
        resetTurnstile();
      }
    }
  };

  return (
    <>
      <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6">
        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
          <div className="px-6 py-6 sm:px-12">
            {!isSubmittedAndUserCreated ? (
              <>
                <h2 className="mb-8 text-center text-2xl font-bold leading-5 tracking-tight text-gray-900 border-b border-gray-200 pb-4">
                  Sign In
                </h2>
                <div className="mt-10 gap-3">
                  <a
                    href={`/google-sign-up?redirectUrl=${encodeURIComponent(POST_AUTH_LANDING_PATH)}`}
                    className="flex items-center justify-center rounded-full bg-background-dark px-3 py-2 text-sm font-medium text-black shadow-sm ring-1 ring-inset ring-gray-200 hover:bg-gray-50 focus-visible:ring-transparent"
                  >
                    <svg className="h-6 w-6" aria-hidden="true" viewBox="0 0 24 24">
                      <path
                        d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
                        fill="#EA4335"
                      />
                      <path
                        d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
                        fill="#4285F4"
                      />
                      <path
                        d="M5.26498 14.2949C5.01498 13.5699 4.87498 12.7999 4.87498 11.9999C4.87498 11.1999 5.01498 10.4299 5.26498 9.70492L1.27498 6.60992C0.464983 8.22992 0.000976562 10.0599 0.000976562 11.9999C0.000976562 13.9399 0.464983 15.7699 1.27498 17.3899L5.26498 14.2949Z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12.0004 24C15.2354 24 17.9504 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.855 13.6204 19.325 12.0004 19.325C8.87537 19.325 6.22037 17.215 5.27537 14.295L1.28537 17.39C3.25537 21.31 7.31037 24 12.0004 24Z"
                        fill="#34A853"
                      />
                    </svg>
                    <span className="text-sm font-semibold mx-2">Google Sign In</span>
                  </a>
                </div>
                <div className="relative my-6">
                  <div aria-hidden="true" className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-sm font-medium leading-6">
                    <span className="bg-white px-6 text-gray-900">OR</span>
                  </div>
                </div>
                <Form onSubmit={handleSubmit} className="space-y-6">
                  <div className="mt-2">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={email}
                      onChange={handleEmailChange}
                      placeholder="Enter your email address"
                      className={`block w-full font-semibold rounded-md border-0 py-3 text-gray-900 shadow-sm ring-1 ring-inset ${message ? 'ring-red-500' : 'ring-gray-300'
                        } placeholder:text-gray-400 placeholder:font-normal focus:ring-2 focus:ring-inset ${message ? 'focus:ring-red-500' : 'focus:ring-green-900'
                        } sm:text-base sm:leading-6`}
                    />
                    {message && (
                      <p className="mt-2 text-sm text-red-600">{message}</p>
                    )}
                  </div>
                  <div>
                    <Button
                      type="primary"
                      size="medium"
                      fullWidth={true}
                    >
                      Sign In
                    </Button>
                  </div>
                  <PasswordlessAuthCallout />
                  {/* Cloudflare Turnstile Widget — shown after valid email is entered */}
                  {turnstileSiteKey && isValidEmail && (
                    <div className="flex justify-center">
                      <div ref={turnstileContainerRef} />
                    </div>
                  )}
                </Form>
                <p className="mt-10 text-center text-sm text-gray-500">
                  Don&apos;t have an account?
                  <Link to="/sign-up" className="font-bold leading-6 text-green-700 ms-2">
                    Get Started
                  </Link>
                </p>
              </>
            ) : (
              <div className="text-center">
                <p className="mb-4">{message}</p>
                <p>Still missing? <a href="/sign-in">try again</a> or contact us at <b
                  className="text-muted">support@marketdatafyi.com</b></p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export function ErrorBoundary() {
  return (
    <div className="text-center">
      <p className="mb-4">An unexpected error occurred</p>
      <p>
        contact us at{" "}
        <b className="text-gray-600">support@marketdatafyi.com</b>
      </p>
    </div>
  );
}

export default SignIn;